import * as THREE from 'three';
import React from 'react';
import EffectorBase, { EffectorPropertyDrawer, LoopableEffector, ReversibleEffector } from "./EffectorBase";
import { CustomEventTypes, EasingFunctionTypes, PropertyTypes, SaveModificationTypes, Space } from "../../../globals";
import Vector3Property from "../../../components/properties/Vector3Property";
import DropdownProperty from "../../../components/properties/DropdownProperty";
import Engine from '../../Engine';
import ProjectSaver from '../../ProjectSaver';
import TransformUtils from '../../utilities/TransformUtils';
import { TimelineFragment } from '../TimelineFragment';
import { areArraysEqual } from '../../utilities/Misc';

interface EffectorTransformTranslateSaveData
{
	translationAmount: { x: number, y: number, z: number };
	easing: EasingFunctionTypes;
	reversed: boolean;
	loopCount: number;
	space: Space;
}

export default class EffectorTransformTranslate extends EffectorBase<EffectorTransformTranslate> implements LoopableEffector, ReversibleEffector
{
	translation: THREE.Vector3;
	deltaTranslation: THREE.Vector3;
	easing: EasingFunctionTypes;
	reversed: boolean;
	loopCount: number;
	space: Space;
	defaultValues: { translation: { x: number, y: number, z: number } };

	constructor(fragment: TimelineFragment)
	{
		super(fragment);

		this.translation = new THREE.Vector3(0, 0, 0);
		this.deltaTranslation = new THREE.Vector3(0, 0, 0);
		this.easing = EasingFunctionTypes.Linear;
		this.reversed = false;
		this.space = Space.World;
		this.loopCount = 1;
		this.defaultValues = {
			translation: { x: 0, y: 0, z: 0 },
		};	    
	}

	save = () : EffectorTransformTranslateSaveData=>
	{
		const output = {
			translationAmount: { x: this.translation.x, y: this.translation.y, z: this.translation.z },
			easing: this.easing,
			reversed: this.reversed,
			loopCount: this.loopCount,
			space: this.space,
		};

		return output;
	};

	load = (savedData: EffectorTransformTranslateSaveData) =>
	{
		this.translation.set(savedData.translationAmount.x, savedData.translationAmount.y, savedData.translationAmount.z);
		if (savedData.easing) this.easing = savedData.easing.charAt(0).toLowerCase() + savedData.easing.slice(1) as EasingFunctionTypes;
		if (savedData.reversed) this.reversed = savedData.reversed;
		if (savedData.loopCount) this.loopCount = savedData.loopCount;
		if (savedData.space) this.space = savedData.space;
	};

	copyValues = (sourceEffector: EffectorTransformTranslate) =>
	{
		this.translation.copy(sourceEffector.translation);
		this.easing = sourceEffector.easing;
		this.reversed = sourceEffector.reversed;
		this.loopCount = sourceEffector.loopCount;
		this.space = sourceEffector.space;
	}

	setTranslation = (x: number, y: number, z: number) =>
	{
		if (typeof (x) === "number" && !isNaN(x)) this.translation.x = x;
		if (typeof (y) === "number" && !isNaN(y)) this.translation.y = y;
		if (typeof (z) === "number" && !isNaN(z)) this.translation.z = z;
	}

	resetState = () =>
	{
		if (!this.fragment.previousTime) return;

		this.deltaTranslation.negate();
		this.forEachTargetObject((targetObject) =>
		{
			if (this.space === Space.World)
			{
				TransformUtils.translateWorldSpace(targetObject, this.deltaTranslation);
			}
			else
			{
				const localTrasnaltion = new THREE.Vector3().copy(this.deltaTranslation).applyQuaternion(targetObject.quaternion);
				targetObject.position.add(localTrasnaltion);
			}
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectPositionChanged);
		}
	}

	update = (fragmentTime: number) =>
	{
		if (!fragmentTime) return;

		this.deltaTranslation.copy(this.translation).multiplyScalar(fragmentTime);
		this.forEachTargetObject((targetObject) =>
		{
			if (this.space === Space.World)
			{
				TransformUtils.translateWorldSpace(targetObject, this.deltaTranslation);
			}
			else
			{
				const localTrasnaltion = new THREE.Vector3().copy(this.deltaTranslation).applyQuaternion(targetObject.quaternion);
				targetObject.position.add(localTrasnaltion);
			}
		});

		if (Engine.getInstance().getPickingManager().getCurrentSelection().objects.length)
		{
			this.fragment.timeline.pushEndOfFrameEvent(CustomEventTypes.selectedObjectPositionChanged);
		}
	}

	onAdded = () =>
	{
		for (const object of this.targetObjects())
		{
			ProjectSaver.getInstance().markModifiedObject(object, SaveModificationTypes.Position);
		}
	}

	onDelete = () =>
	{
		super.onDelete();
	}

	getEditorProperties = () =>
	{
		return [
			...this.getCommonPropertyDrawers(),
			new EffectorPropertyDrawer(
				"Translation",
				PropertyTypes.Vector3,
				<Vector3Property
					value={[this.translation.x, this.translation.y, this.translation.z]}
					unitScaling={true}
					step={0.1}
					renderChildrenInSeparateRows={true}
					defaultValue={this.defaultValues.translation}
					decimals={3}
				/>,
				() => {return [this.translation.x, this.translation.y, this.translation.z];},
				(v) => {this.setTranslation(v.x, v.y, v.z);},
				(a, b) => {return areArraysEqual(a, b);},
			),
			new EffectorPropertyDrawer(
				"Space",
				PropertyTypes.DropDown,
				<DropdownProperty
					data-name={"property-space"}
					value={this.space}
					options={
						Object.entries(Space).map(([key, value]) =>
						{
							return { display: key, value: value };
						})
					}
				/>,
				() => {return this.space;},
				(v) => {this.space = v;},
			),
		];
	}
}
